import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import {
  ContentCopy,
  Refresh,
  Search,
  CheckCircle,
  People
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { 
  createReferralCode, 
  getReferralCodes,
  getReferralStats 
} from '../../services/adminService';

const MotionCard = motion(Card);

function ReferralManagement() {
  const [referralName, setReferralName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralCodes, setReferralCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadReferralCodes();
  }, []);

  const loadReferralCodes = async () => {
    try {
      const codes = await getReferralCodes();
      const codesWithStats = await Promise.all(
        codes.map(async (code) => {
          const stats = await getReferralStats(code.id);
          return { ...code, ...stats };
        })
      );
      setReferralCodes(codesWithStats);
    } catch (error) {
      console.error('Error loading referral codes:', error);
    }
  };

  const generateReferralCode = () => {
    const prefix = 'AIRUP-';
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = prefix;
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setReferralCode(result);
  };

  const handleCreateReferral = async () => {
    if (!referralName || !referralCode) return;
    
    try {
      await createReferralCode({
        name: referralName,
        code: referralCode,
        createdAt: new Date(),
        usageCount: 0
      });
      setReferralName('');
      setReferralCode('');
      loadReferralCodes();
    } catch (error) {
      console.error('Error creating referral code:', error);
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredCodes = referralCodes.filter(ref =>
    ref.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ref.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Yeni Referans Kodu Oluşturma */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Yeni Referans Kodu Oluştur
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Referans Adı"
              value={referralName}
              onChange={(e) => setReferralName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Referans Kodu"
              value={referralCode}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={generateReferralCode}>
                      <Refresh />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleCreateReferral}
              disabled={!referralName || !referralCode}
              sx={{
                height: 56,
                background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
              }}
            >
              Oluştur
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* İstatistik Kartları */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <MotionCard
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            sx={{ 
              background: 'linear-gradient(135deg, rgba(0, 191, 13, 0.1) 0%, rgba(76, 241, 61, 0.1) 100%)',
            }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {referralCodes.length}
              </Typography>
              <Typography color="text.secondary">
                Toplam Referans Kodu
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <MotionCard
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            sx={{ 
              background: 'linear-gradient(135deg, rgba(0, 191, 13, 0.1) 0%, rgba(76, 241, 61, 0.1) 100%)',
            }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {referralCodes.reduce((acc, code) => acc + (code.usageCount || 0), 0)}
              </Typography>
              <Typography color="text.secondary">
                Toplam Kullanım
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* Referans Kodları Tablosu */}
      <Paper sx={{ p: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Referans kodu veya isim ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Referans Adı</TableCell>
                <TableCell>Kod</TableCell>
                <TableCell>Oluşturulma Tarihi</TableCell>
                <TableCell>Kullanım Sayısı</TableCell>
                <TableCell>Kullanan Kullanıcılar</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCodes.map((ref) => (
                <TableRow key={ref.id}>
                  <TableCell>{ref.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={ref.code}
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(ref.createdAt.toDate()).toLocaleDateString('tr-TR')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<People />}
                      label={ref.usageCount || 0}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {ref.users?.map(user => (
                      <Chip
                        key={user.id}
                        avatar={<Avatar>{user.username.charAt(0)}</Avatar>}
                        label={user.username}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={copied === ref.code ? "Kopyalandı!" : "Kopyala"}>
                      <IconButton onClick={() => copyToClipboard(ref.code)}>
                        {copied === ref.code ? <CheckCircle color="success" /> : <ContentCopy />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default ReferralManagement; 